/** Authored job changes, independent of the legacy all-fields confirmed assessment.
 * Omitted risk pairs preserve historical scores (including unfactorable scores).
 * These strict writes never manufacture confirmation from catalogue defaults. */
import { z } from "zod";

export const RamsHazardRiskPairSchema = z.object({
  likelihood: z.number().int().min(1).max(5),
  severity: z.number().int().min(1).max(5),
}).strict();
export type RamsHazardRiskPair = z.infer<typeof RamsHazardRiskPairSchema>;
export const RamsHazardEditSchema = z.object({
  version: z.literal(1),
  /** Explicitly discard prior job overrides before applying this partial edit.
   * Consumed on publication; never inferred from omitted risk pairs. */
  resetToSource:z.literal(true).optional(),
  name: z.string().trim().min(1).max(200).optional(),
  description: z.string().max(10000).optional(),
  whoAtRisk: z.string().max(2000).optional(),
  controls: z.string().max(10000).optional(),
  ppeRequired: z.string().max(4000).optional(),
  startingRisk: RamsHazardRiskPairSchema.optional(),
  residualRisk: RamsHazardRiskPairSchema.optional(),
}).strict();
export type RamsHazardEdit = z.infer<typeof RamsHazardEditSchema>;
export const RamsHazardIdentitySchema = z.string().min(1).max(200).regex(/^[a-zA-Z0-9_-]+$/)
  .refine((key) => !["__proto__", "constructor", "prototype"].includes(key), "Invalid hazard identity.");
function boundedMap<T extends z.ZodType>(schema: T) {
  return z.preprocess((value, context) => {
    if (!value || typeof value !== "object" || Array.isArray(value) ||
      Object.keys(value).length > 500 ||
      Object.keys(value).some((key) => ["__proto__", "constructor", "prototype"].includes(key)) ||
      new TextEncoder().encode(JSON.stringify(value)).byteLength > 1024 * 1024) {
      context.addIssue({ code: "custom", message: "RAMS hazard edits exceed the document limit or are invalid." });
      return z.NEVER;
    }
    return value;
  }, z.record(RamsHazardIdentitySchema, schema));
}
export const RamsHazardEditsSchema = boundedMap(RamsHazardEditSchema);
export const RamsHazardEditReadSchema = RamsHazardEditSchema.extend({
  startingRisk: RamsHazardRiskPairSchema.strip().optional(),
  residualRisk: RamsHazardRiskPairSchema.strip().optional(),
}).strip();
export const RamsHazardEditsReadSchema = boundedMap(RamsHazardEditReadSchema);
export type RamsHazardEdits = z.infer<typeof RamsHazardEditsSchema>;
export const RamsHazardEditEditorDraftSchema = z.object({
  version:z.literal(1),slug:RamsHazardIdentitySchema,
  form:z.object({name:z.string().max(200),description:z.string().max(10000),whoAtRisk:z.string().max(2000),
    controls:z.string().max(10000),ppeRequired:z.string().max(4000),
    likelihood:z.string().regex(/^[1-5]?$/),severity:z.string().regex(/^[1-5]?$/),
    residualLikelihood:z.string().regex(/^[1-5]?$/),residualSeverity:z.string().regex(/^[1-5]?$/),
    changeStarting:z.boolean(),changeResidual:z.boolean(),reset:z.boolean(),
  }).strict(),
}).strict();
export type RamsHazardEditEditorDraft = z.infer<typeof RamsHazardEditEditorDraftSchema>;
export const RamsHazardEditReceiptSchema = z.object({operationId:z.string().uuid(),digest:z.string().regex(/^[a-f0-9]{64}$/)}).strict();

/** Frozen effective rows carry stable identities and original block metadata.
 * They are not full confirmed assessments and must never be relabelled as one. */
export const RamsEffectiveHazardSnapshotSchema = z.object({
  key: RamsHazardIdentitySchema,
  hazardId: z.string().min(1).max(200).optional(),
  category: z.string().min(1).max(200),
  name: z.string().min(1).max(200), description: z.string().max(10000),
  whoAtRisk: z.string().max(2000), controls: z.string().max(10000), ppeRequired: z.string().max(4000),
  startingRisk: RamsHazardRiskPairSchema.optional(), residualRisk: RamsHazardRiskPairSchema.optional(),
  startingScore: z.number().int().min(1).max(25).optional(), residualScore: z.number().int().min(1).max(25).optional(),
  startingBand: z.string().max(40).optional(), residualBand: z.string().max(40).optional(),
  defaultRiskRating: z.string().max(200).nullable(),
  controlBlocks: z.array(z.object({ title:z.string().max(1000), controlText:z.string().max(10000),
    ppeRequired:z.string().max(4000).nullable(), methodSteps:z.string().max(10000).nullable() }).strict()).max(500),
}).strict().superRefine((row,context)=>{
  if(row.startingRisk && row.startingScore!==row.startingRisk.likelihood*row.startingRisk.severity)
    context.addIssue({code:"custom",message:"Starting risk does not match its recorded factors."});
  if(row.residualRisk && row.residualScore!==row.residualRisk.likelihood*row.residualRisk.severity)
    context.addIssue({code:"custom",message:"Residual risk does not match its recorded factors."});
});
export type RamsEffectiveHazardSnapshot = z.infer<typeof RamsEffectiveHazardSnapshotSchema>;
export const RamsEffectiveHazardsSnapshotSchema = z.preprocess((value, context) => {
  if (!Array.isArray(value) || value.length > 500 || new TextEncoder().encode(JSON.stringify(value)).byteLength > 1024 * 1024) {
    context.addIssue({code:"custom",message:"RAMS effective hazards exceed the document limit."}); return z.NEVER;
  }
  return value;
}, z.array(RamsEffectiveHazardSnapshotSchema).refine((rows) => new Set(rows.map((row) => row.key)).size === rows.length));

const RamsEffectiveHazardSnapshotReadSchema=RamsEffectiveHazardSnapshotSchema.safeExtend({
  startingRisk:RamsHazardRiskPairSchema.strip().optional(),residualRisk:RamsHazardRiskPairSchema.strip().optional(),
  controlBlocks:z.array(RamsEffectiveHazardSnapshotSchema.shape.controlBlocks.element.strip()).max(500),
}).strip();
/** Response-only tolerance is independent of strict persisted/wire validation. */
export const RamsEffectiveHazardsSnapshotReadSchema=z.preprocess((value,context)=>{
  if(!Array.isArray(value)||value.length>500||new TextEncoder().encode(JSON.stringify(value)).byteLength>1024*1024){
    context.addIssue({code:"custom",message:"RAMS effective hazards exceed the document limit."});return z.NEVER;
  }
  return value;
},z.array(RamsEffectiveHazardSnapshotReadSchema).refine(rows=>new Set(rows.map(row=>row.key)).size===rows.length));
